import React, { useState, useEffect, useRef } from "react";
import { Input } from "../ui/input";

/**
 * QuantityInput — Campo numérico de cantidad con edición fluida y validación en vivo.
 *
 * Resuelve el problema clásico del input `type="number"` que hace clamp al mínimo en
 * cada pulsación (`Math.max(min, parseInt(value)||min)` sobre `value`), lo que impide
 * borrar el campo para reescribir un número y provoca reseteos al teclear.
 *
 * Estrategia: mantiene un estado de texto interno. Mientras el usuario edita:
 *  - permite el campo vacío de forma transitoria (no fuerza el mínimo);
 *  - propaga el valor numérico en vivo vía `onChange` cuando hay un número válido
 *    (así el precio/total se actualiza en tiempo real = validación en vivo);
 *  - no deja que una resincronización del padre pise lo que se está escribiendo
 *    (se ignora la sincronización mientras el input tiene el foco).
 * Al perder el foco (blur) o si el campo quedó vacío, hace clamp al mínimo `min`.
 *
 * @param {number}   props.value          Valor numérico controlado desde el padre.
 * @param {Function} props.onChange       (number) => void — recibe el valor numérico ya parseado y con clamp a `min`.
 * @param {number}   [props.min=1]        Mínimo permitido (se aplica en blur y cuando el campo queda vacío).
 * @param {string}   [props.dataTestId]   `data-testid` del input.
 * @param {string}   [props.className]    Clases extra para el input.
 * @param {string}   [props.placeholder]  Placeholder cuando el campo está vacío.
 */
export const QuantityInput = ({
  value,
  onChange,
  min = 1,
  dataTestId,
  className = "",
  placeholder,
}) => {
  const numToText = (n) =>
    n === null || n === undefined || Number.isNaN(n) ? "" : String(n);

  const [text, setText] = useState(numToText(value));
  const focusedRef = useRef(false);

  // Re-sync from the parent only when the user is NOT editing, so external
  // changes (e.g. PackageBuilder regenerating rows) are reflected without
  // fighting the in-progress keystrokes.
  useEffect(() => {
    if (focusedRef.current) return;
    setText(numToText(value));
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    setText(raw);
    if (raw === "") return; // allow transient empty; clamp happens on blur
    onChange(Math.max(min, parseInt(raw, 10)));
  };

  const handleFocus = () => {
    focusedRef.current = true;
  };

  const handleBlur = () => {
    focusedRef.current = false;
    const parsed = parseInt(text, 10);
    const clamped = Number.isFinite(parsed) ? Math.max(min, parsed) : min;
    setText(numToText(clamped));
    onChange(clamped);
  };

  return (
    <Input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      data-testid={dataTestId}
      className={className}
    />
  );
};
